import { useState, useEffect } from "react";
import axios from "axios";
import { FiActivity, FiChevronDown, FiRefreshCw, FiLock, FiChevronUp } from "react-icons/fi";
import { MdOutlineInventory2 } from "react-icons/md";
import { CiTempHigh } from "react-icons/ci";
import Header from "../component/Header";
import { API_BASE_URL } from "../utils/apiConfig";

/* inventory moved to dynamic API */

const Progress = ({ label, value }) => {
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between text-[11px] text-white/90 mb-1 font-medium">
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1 h-1.5 bg-gray-900 rounded-full overflow-hidden border border-white/5">
          <div
            className="h-full bg-green-400 transition-all duration-700 shadow-[0_0_10px_rgba(74,222,128,0.3)]"
            style={{ width: `${value}%` }}
          />
        </div>
        <span className="text-[11px] font-bold text-green-400 min-w-[32px] text-right">
          {value}%
        </span>
      </div>
    </div>
  );
};

function Machines() {
  const [openStates, setOpenStates] = useState({});
  const [devices, setDevices] = useState([]);
  const [orderHistories, setOrderHistories] = useState({});
  const [deviceStocks, setDeviceStocks] = useState({});
  const [loading, setLoading] = useState(true);
  const userRole = localStorage.getItem("role");

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/api/devices`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDevices(response.data);

        // Fetch stocks for all devices
        response.data.forEach(device => {
          fetchDeviceStock(device.device_id_PK);
        });

      } catch (err) {
        console.error("Fetch failed", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDevices();
  }, []);

  const fetchDeviceStock = async (deviceId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/machine-flavor-stock/device/${deviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Response is { status: true, flavors: [...], bases: [...] }
      setDeviceStocks(prev => ({ ...prev, [deviceId]: response.data }));
    } catch (err) {
      console.error("Failed to fetch device stock", err);
    }
  };

  const fetchOrderHistory = async (deviceId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/orders/device/${deviceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Set the orders list
      setOrderHistories(prev => ({ ...prev, [deviceId]: response.data.orders }));

      // Update today's and total sales for this specific device
      setDevices(prevDevices => prevDevices.map(device =>
        device.device_id_PK === deviceId ? {
          ...device,
          today_sales: response.data.todaySales,
          total_sales: response.data.totalSales
        } : device
      ));
    } catch (err) {
      console.error("Failed to fetch order history", err);
    }
  };

  const toggleOpen = (id) => {
    if (!openStates[id]) {
      fetchOrderHistory(id);
    }
    setOpenStates(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) return <div className="bg-black min-h-screen text-white flex items-center justify-center">Loading Machines...</div>;

  return (
    <div className="bg-black min-h-screen text-white p-6">
      <Header />

      <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-6 mt-8 ">
        {devices.map((device) => (
          <div key={device.device_id_PK} className="relative bg-[#111211] rounded-2xl p-6 border border-gray-800 shadow-2xl mb-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-3xl font-bold text-green-500 tracking-tight">{device.device_name}</h2>
                <p className="text-gray-500 text-[11px] font-medium uppercase tracking-wider">{device.location}</p>
              </div>

              <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold border transition-shadow shadow-[0_0_15px_rgba(74,222,128,0.1)] ${device.status === 'ACTIVE' ? 'border-green-500/50 text-green-500' : 'border-red-500/50 text-red-500'}`}>
                <FiActivity className={device.status === 'ACTIVE' ? "animate-pulse" : ""} />
                {device.status}
              </span>
            </div>

            <div className="relative rounded-2xl border border-green-500/10 bg-gradient-to-br from-[#1a2e1a] to-[#0d140d] p-6 mb-8 text-center shadow-inner">
              <div className="grid grid-cols-2 gap-8 divide-x divide-white/5">
                <div className="flex flex-col items-center">
                  <p className="text-[10px] text-gray-400 uppercase font-bold tracking-[0.2em] mb-2 font-mono">Today’s Sales</p>
                  <p className="text-green-400 text-3xl font-black drop-shadow-[0_0_20px_rgba(74,222,128,0.4)]">₹{device.today_sales}</p>
                </div>
                <div className="flex flex-col items-center">
                  <p className="text-[10px] text-gray-400 uppercase font-bold tracking-[0.2em] mb-2 font-mono">Total Sales</p>
                  <p className="text-white/90 text-3xl font-black drop-shadow-[0_0_20px_rgba(255,255,255,0.1)]">₹{device.total_sales}</p>
                </div>
              </div>

              <button
                onClick={() => toggleOpen(device.device_id_PK)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500/30 hover:text-green-400 transition"
              >
                {openStates[device.device_id_PK] ? <FiChevronUp size={24} /> : <FiChevronDown size={24} />}
              </button>
            </div>

            {openStates[device.device_id_PK] && (
              <div className="mt-4 mb-6 p-4 bg-black/40 rounded-xl border border-gray-800 overflow-x-auto">
                <h3 className="text-sm font-semibold text-green-400 mb-3 ml-1">Recent Order History</h3>
                <table className="min-w-full text-[10px] md:text-sm text-gray-300">
                  <thead>
                    <tr className="border-b border-gray-800 text-gray-500 uppercase">
                      <th className="px-2 py-2 text-left">S.No</th>
                      <th className="px-2 py-2 text-left">Flavours</th>
                      <th className="px-2 py-2 text-left">Base</th>
                      <th className="px-2 py-2 text-left">Size</th>
                      <th className="px-2 py-2 text-left">status</th>
                      <th className="px-2 py-2 text-left">Amount</th>
                      <th className="px-2 py-2 text-left">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(orderHistories[device.device_id_PK] || []).slice(0, 5).map((order, i) => (
                      <tr key={order.order_id_PK} className="border-b border-gray-800/50">
                        <td className="px-2 py-2 text-white font-mono">{i + 1}</td>
                        <td className="px-2 py-2">{order.flavor_name}</td>
                        <td className="px-2 py-2">{order.base_name}</td>
                        <td className="px-2 py-2">{order.shake_size}</td>
                        <td className="px-2 py-2">{order.status}</td>
                        <td className="px-2 py-2 text-green-400 font-bold">₹{order.total_price}</td>
                        <td className="px-2 py-2 text-gray-500">{new Date(order.created_at).toLocaleTimeString()}</td>
                      </tr>
                    ))}
                    {(!orderHistories[device.device_id_PK] || orderHistories[device.device_id_PK].length === 0) && (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-gray-600 italic">No recent transactions found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-xs text-gray-400">Shakes</p>
                <p className="text-white font-bold">{device.shakes_today}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Temp</p>
                <p className="flex items-center gap-1 text-white font-bold">
                  <CiTempHigh className="text-blue-400 text-lg" />
                  {device.temp}°C
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Last Clean</p>
                <p className="text-white font-bold">{device.last_cleaned || 'N/A'}</p>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between text-green-400 text-[10px] font-bold uppercase tracking-widest mb-4 opacity-80">
                <div className="flex items-center gap-2">
                  <MdOutlineInventory2 size={14} />
                  Machine Inventory - Powder Stock
                </div>
                <button
                  onClick={() => window.location.href = `/inventoryPage?deviceId=${device.device_id_PK}`}
                  className="text-[9px] text-gray-500 hover:text-green-400 transition"
                >
                  Manage
                </button>
              </div>

              {/* Flavors: Full Width */}
              <div className="space-y-1 mb-4">
                {deviceStocks[device.device_id_PK]?.flavors?.length > 0 ? (
                  deviceStocks[device.device_id_PK].flavors.map((stock, idx) => (
                    <Progress key={idx} label={stock.flavor_name} value={stock.stock} type="powder" />
                  ))
                ) : (
                  <p className="text-[10px] text-gray-500 italic opacity-50">No powder stock data</p>
                )}
              </div>

              {/* Bases: 2-Column Grid */}
              <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                {deviceStocks[device.device_id_PK]?.bases?.length > 0 &&
                  deviceStocks[device.device_id_PK].bases.map((base, idx) => (
                    <Progress key={idx} label={base.base_name} value={base.stock} type="liquid" />
                  ))
                }
              </div>
            </div>

            {userRole === 'superadmin' && (
              <div className="mt-5 flex items-center gap-3">
                <button className="flex-1 flex items-center justify-center gap-2 px-4 py-1.5 rounded-lg border border-green-400/30 text-green-400 hover:bg-green-400 hover:text-black transition text-xs font-bold uppercase tracking-widest">
                  ✨ Clean
                </button>
                <button className="p-2.5 rounded-lg bg-black border border-gray-800 hover:border-green-400/50 transition">
                  <FiLock className="text-gray-400" size={14} />
                </button>
                <button className="p-2.5 rounded-lg bg-black border border-gray-800 hover:border-green-400/50 transition">
                  <FiRefreshCw className="text-gray-400" size={14} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

    </div>

  )
}

export default Machines;

